import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, User, Tag, Clock } from 'lucide-react';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

const estimateReadTime = (content) => {
  const words = (content || '').split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${API}/blog/${slug}`);
        setPost(res.data);
      } catch {
        setPost(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-36 pb-24 flex items-center justify-center">
        <p className="text-muted-foreground font-sans">Loading article...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-36 pb-24 text-center" data-testid="blog-post-not-found">
        <h1 className="font-serif text-3xl font-light mb-4">Article Not Found</h1>
        <p className="text-muted-foreground font-sans mb-6">The article you're looking for doesn't exist or has been removed.</p>
        <Link to="/blog" className="text-accent font-sans text-sm hover:underline">
          &larr; Back to Blog
        </Link>
      </div>
    );
  }

  const readTime = estimateReadTime(post.content);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt || (post.content || '').substring(0, 200),
    "author": { "@type": "Person", "name": post.author, "@id": `${BASE_URL}/#artist` },
    "publisher": { "@type": "Person", "name": post.author },
    "datePublished": post.published_at || post.created_at,
    "dateModified": post.updated_at || post.created_at,
    "url": `${BASE_URL}/blog/${post.slug}`,
    "mainEntityOfPage": `${BASE_URL}/blog/${post.slug}`,
    "keywords": (post.tags || []).join(', '),
    "articleSection": post.category,
    "wordCount": (post.content || '').split(/\s+/).length,
    ...(post.cover_image && !post.cover_image.startsWith('data:') ? { image: post.cover_image } : {}),
  };

  const ogImageUrl = `${API}/blog/${post.slug}/og-image`;

  return (
    <div className="min-h-screen pt-28 sm:pt-36 pb-16 sm:pb-24" data-testid="blog-post-page">
      <SEO
        title={`${post.title} - Gangadhar Som`}
        description={post.excerpt || (post.content || '').substring(0, 160)}
        keywords={(post.tags || []).join(', ') + ', Gangadhar Som, art blog'}
        url={`${BASE_URL}/blog/${post.slug}`}
        type="article"
        image={ogImageUrl}
        schema={articleSchema}
      />
      <article className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Back link */}
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-accent mb-8 transition-colors font-sans" data-testid="back-to-blog-link">
          <ArrowLeft size={16} /> Back to Blog
        </Link>

        {/* Header */}
        <header className="mb-10">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-[10px] font-sans uppercase tracking-[0.15em] text-accent font-medium px-2.5 py-1 bg-accent/10 rounded-full">
              {post.category}
            </span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-5" data-testid="blog-post-title">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground font-sans">
            <span className="flex items-center gap-1.5">
              <User size={14} /> {post.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar size={14} /> {formatDate(post.published_at || post.created_at)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={14} /> {readTime} min read
            </span>
          </div>
        </header>

        {/* Cover Image */}
        {post.cover_image && (
          <div className="rounded-lg overflow-hidden mb-10 bg-muted">
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full object-cover max-h-[500px]"
              data-testid="blog-post-cover"
            />
          </div>
        )}

        {/* Content */}
        <div
          className="prose prose-stone max-w-none font-sans text-base leading-relaxed [&>p]:mb-5 [&>h2]:font-serif [&>h2]:text-2xl [&>h2]:font-light [&>h2]:mt-10 [&>h2]:mb-4 [&>h3]:font-serif [&>h3]:text-xl [&>h3]:font-light [&>h3]:mt-8 [&>h3]:mb-3 [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>blockquote]:border-l-4 [&>blockquote]:border-accent [&>blockquote]:pl-5 [&>blockquote]:italic [&>blockquote]:text-muted-foreground"
          data-testid="blog-post-content"
          dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, '<br/>') }}
        />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-10 pt-6 border-t border-border" data-testid="blog-post-tags">
            <div className="flex flex-wrap items-center gap-2">
              <Tag size={14} className="text-muted-foreground" />
              {post.tags.map((tag, i) => (
                <Link
                  key={i}
                  to={`/blog?tag=${tag}`}
                  className="text-xs px-2.5 py-1 bg-accent/10 text-accent rounded-full font-sans hover:bg-accent/20 transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Author Card */}
        <div className="mt-10 pt-6 border-t border-border">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
              <User size={20} className="text-accent" />
            </div>
            <div>
              <p className="font-serif text-lg font-light">{post.author}</p>
              <p className="text-xs text-muted-foreground font-sans">Artist & Writer</p>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
